import compose from 'lodash/fp/compose';
import React from 'react';
import { PageType } from '@hotelplan/graphql.types';
import { getObjectIdPageProp } from '@hotelplan/libs.context.object-id';
import { objectIdUnitRef } from '@hotelplan/libs.object-id-api';
import { createOpenGraphProp } from '@hotelplan/libs.open-graph';
import { apolloReqCtxUnitRef } from '@hotelplan/libs.req-ctx-api';
import { withSearchStatePageContext } from '@hotelplan/libs.search-state';
import { ServerSideUnit, ServerSideUnitRef } from '@hotelplan/libs.ssp-units';
import { trimUrlQueryParams } from '@hotelplan/libs.url';
import { Container } from 'components/core/container';
import { mapSrlFilterValuesToSrlFilterCriteriaInput } from 'components/domain/filters/Filters.mappers';
import {
  mapSearchControlToCriteria,
  mapSearchStatePayloadToCriteria,
} from 'components/domain/search-control/components/SearchControlForm/SearchControl.mappers';
import SeoText from 'components/domain/seo-text';
import { ISrlState } from 'components/domain/srl/SRL.types';
import ThemePageLead from 'components/domain/theme/ThemePageLead';
import ThemeSrl from 'components/domain/theme/ThemeSrl';
import { createPageServerFn, serverCorrectUrlUnit } from 'config';
import { themeSearchStateUnit } from 'config/theme/themeSearchStateUnit';
import {
  GetThemePageContentDocument,
  GetThemePageContentQuery,
  GetThemePageContentQueryVariables,
} from 'graphql/theme/GetThemePageContent.generated';
import {
  GetThemePageMetaDataDocument,
  GetThemePageMetaDataQuery,
  GetThemePageMetaDataQueryVariables,
} from 'graphql/theme/GetThemePageMetaData.generated';
import {
  GetThemeToSrlUrlDocument,
  GetThemeToSrlUrlQuery,
  GetThemeToSrlUrlQueryVariables,
} from 'graphql/theme/GetThemeToSrlUrl.generated';
import {
  GetThemeUrlDocument,
  GetThemeUrlQuery,
  GetThemeUrlQueryVariables,
} from 'graphql/theme/GetThemeUrl.generated';

interface IThemePageProps {
  content: GetThemePageContentQuery['theme'];
}

function ThemePage({ content }: IThemePageProps): React.ReactElement {
  const { information, hero, seoText, disrupterObject } =
    content?.staticContent || {};

  return (
    <Container>
      <ThemePageLead
        information={information}
        hero={hero}
        disrupterObject={disrupterObject}
      >
        <ThemeSrl objectName={information.title} />
      </ThemePageLead>
      {seoText ? <SeoText seoText={seoText} /> : null}
    </Container>
  );
}

const serverThemeCorrectUrlUnit = serverCorrectUrlUnit<
  GetThemeUrlQuery,
  GetThemeUrlQueryVariables
>({
  query: GetThemeUrlDocument,
  getVariables: (ctx, { id }, pageType, routePayload, searchStatePayload) => {
    return {
      id,
      criteria: mapSearchStatePayloadToCriteria<ISrlState>(searchStatePayload),
    };
  },
  getUrl: data => trimUrlQueryParams(data.theme.link),
  withSearchState: true,
});

const serverThemeContentUnit = ({ ref }: { ref: string }) => {
  return ServerSideUnit.depsOf([apolloReqCtxUnitRef, objectIdUnitRef])(
    async function (ctx, apollo, objectId) {
      const { queryCtx } = apollo;
      const { id } = objectId;

      const [{ data: metaData }, { data: themeContent }] = await Promise.all([
        queryCtx<GetThemePageMetaDataQuery, GetThemePageMetaDataQueryVariables>(
          {
            query: GetThemePageMetaDataDocument,
            variables: { id },
          }
        ),
        queryCtx<GetThemePageContentQuery, GetThemePageContentQueryVariables>({
          query: GetThemePageContentDocument,
          variables: { id },
        }),
      ]);

      const hero = themeContent?.theme.staticContent.hero.resized[1].url;

      return {
        props: {
          meta: metaData.theme.meta,
          content: themeContent.theme,
          ...createOpenGraphProp({ image: hero }),
        },
      };
    }
  ).withRef(new ServerSideUnitRef<any>(ref));
};

export const getServerSideProps = createPageServerFn(
  {
    namespaceRequired: ['theme', 'map', 'results'],
    pageEventType: 'themes', // todo: supply proper `pageEventType`
    pageType: PageType.ThemeSrl,
  },
  [
    themeSearchStateUnit,
    serverThemeCorrectUrlUnit,
    serverThemeContentUnit({ ref: 'theme-ref' }),
  ]
);

export default compose(
  withSearchStatePageContext<
    ISrlState,
    GetThemeToSrlUrlQuery,
    GetThemeToSrlUrlQueryVariables
  >({
    urlQuery: GetThemeToSrlUrlDocument,
    mapPageStateToUrlQueryVariables: (
      pageState,
      pageProps
    ): GetThemeToSrlUrlQueryVariables => {
      const { searchControl, filters } = pageState;

      // Page id doesn't update correct in pageProps after transition to another theme page
      // so we need to get it from searchControl
      // TODO: investigate why pageProps doesn't update correct (HMWEBDEV-5718)
      const id =
        searchControl.travelDestination.destinations[0].id ||
        getObjectIdPageProp(pageProps).id;

      return {
        id,
        themeSearchCriteriaInput: {
          ...mapSearchControlToCriteria(searchControl),
          filters: mapSrlFilterValuesToSrlFilterCriteriaInput(filters),
        },
      };
    },
    mapUrlQueryResultToInternalLink: ({ theme }) => theme.link,
  })
)(ThemePage);
